import axios from 'axios';
const API_BASE = process.env.REACT_APP_API_URL || '/api';

// ─────────────────────────────────────────────────────────────────────────────
// Availability
// ─────────────────────────────────────────────────────────────────────────────
export interface UbyportAvailabilityStatus {
  available: boolean;
  checkedAt: string | null;
  error: string | null;
}

export const getUbyportAvailability = async (): Promise<UbyportAvailabilityStatus> => {
  const res = await axios.get(`${API_BASE}/ubyport/availability`);
  return res.data;
};
// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
export type UbyportSyncKeyField = 'UBYPORT_ID' | 'KOD2' | 'KOD3' | 'KOD2_KOD3' | 'KOD3_KOD2';

export interface UbyportConfigDTO {
  id: number;
  testingUrl: string;
  productionUrl: string;
  testingModeEnabled: boolean;
  mode: string; // "TESTING" or "PRODUCTION"
  sharedUsername?: string;
  sharedDomain?: string;
  hasSharedPassword?: boolean;
  lastModifiedAt: string;
  lastTestResult?: string;
  lastTestAt?: string;
  stateDisplayColumnCZ?: string;
  stateDisplayColumnEN?: string;
  stateSubmitColumn?: string;
  purposeDisplayColumnCZ?: string;
  purposeDisplayColumnEN?: string;
  purposeSubmitColumn?: string;
  stateSyncKeyField?: UbyportSyncKeyField;
  purposeSyncKeyField?: UbyportSyncKeyField;
  errorSyncKeyField?: UbyportSyncKeyField;
  complianceNoticeCZ?: string;
  complianceNoticeEN?: string;
}
export interface UbyportConfigUpdateDTO {
  testingUrl?: string;
  productionUrl?: string;
  testingModeEnabled?: boolean;
  sharedUsername?: string;
  sharedPassword?: string;
  sharedDomain?: string;
  stateDisplayColumnCZ?: string;
  stateDisplayColumnEN?: string;
  stateSubmitColumn?: string;
  purposeDisplayColumnCZ?: string;
  purposeDisplayColumnEN?: string;
  purposeSubmitColumn?: string;
  stateSyncKeyField?: UbyportSyncKeyField;
  purposeSyncKeyField?: UbyportSyncKeyField;
  errorSyncKeyField?: UbyportSyncKeyField;
  complianceNoticeCZ?: string;
  complianceNoticeEN?: string;
}
export interface UbyportCodeEntryAdminDTO {
  id: number;
  type: string; // STATE, PURPOSE, ERROR
  ubyportId?: number;
  kod2?: string;
  kod3?: string;
  platiOd?: string;
  platiDo?: string;
  textCZ?: string;
  textENG?: string;
  textKratkyCZ?: string;
  textKratkyENG?: string;
  translationCZ?: string;
  translationEN?: string;
  enabled: boolean;
  /** 1 = visa required, 0 = no visa, -1 = not applicable */
  needVisa?: number;
}
export interface UbyportCodeEntryUpdateDTO {
  translationCZ?: string;
  translationEN?: string;
  enabled?: boolean;
  needVisa?: number;
}
export interface UbyportCodeEntryBulkUpdateDTO extends UbyportCodeEntryUpdateDTO {
  id: number;
}
export interface UbyportSyncStatusDTO {
  type: string; // STATE, PURPOSE, ERROR
  lastSyncedAt?: string;
  lastSyncOk: boolean;
  lastSyncError?: string;
}
// ─────────────────────────────────────────────────────────────────────────────
// Configuration Management
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Get current Ubyport configuration
 */
export const getUbyportConfig = async (token: string): Promise<UbyportConfigDTO> => {
  const res = await axios.get(`${API_BASE}/admin/ubyport/config`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};
/**
 * Update Ubyport configuration (URLs and/or mode)
 */
export const updateUbyportConfig = async (
  config: UbyportConfigUpdateDTO,
  token: string
): Promise<UbyportConfigDTO> => {
  const res = await axios.put(`${API_BASE}/admin/ubyport/config`, config, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};
/**
 * Test connection to Ubyport with current configuration
 */
export const testUbyportConnection = async (
  config: UbyportConfigUpdateDTO,
  token: string
): Promise<UbyportConfigDTO> => {
  const res = await axios.post(`${API_BASE}/admin/ubyport/test-connection`, config, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};
// ─────────────────────────────────────────────────────────────────────────────
// Codebook Management
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Get all codebook entries of a specific type
 * @param type STATE, PURPOSE, or ERROR
 * @param token admin JWT token
 * @param includeDisabled if true, include disabled entries
 */
export const getCodebookEntries = async (
  type: string,
  token: string,
  includeDisabled: boolean = false
): Promise<UbyportCodeEntryAdminDTO[]> => {
  const res = await axios.get(`${API_BASE}/admin/ubyport/codebooks/${type}`, {
    params: { includeDisabled },
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};
/**
 * Update app-managed codebook metadata (translations, enabled, visa flag)
 */
export const updateCodebookEntry = async (
  id: number,
  updates: UbyportCodeEntryUpdateDTO,
  token: string
): Promise<UbyportCodeEntryAdminDTO> => {
  const res = await axios.patch(`${API_BASE}/admin/ubyport/codebooks/${id}`, updates, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};

/**
 * Bulk update multiple codebook rows in one request.
 */
export const bulkUpdateCodebookEntries = async (
  updates: UbyportCodeEntryBulkUpdateDTO[],
  token: string
): Promise<UbyportCodeEntryAdminDTO[]> => {
  const res = await axios.patch(`${API_BASE}/admin/ubyport/codebooks/bulk`, updates, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};
/**
 * Toggle the enabled/disabled status of a codebook entry
 */
export const toggleCodebookEntryEnabled = async (
  id: number,
  enabled: boolean,
  token: string
): Promise<UbyportCodeEntryAdminDTO> => {
  const res = await axios.patch(`${API_BASE}/admin/ubyport/codebooks/${id}/enabled`, null, {
    params: { enabled },
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};
// ─────────────────────────────────────────────────────────────────────────────
// Sync Status & Management
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Get sync status for all codebook types
 */
export const getSyncStatus = async (token: string): Promise<UbyportSyncStatusDTO[]> => {
  const res = await axios.get(`${API_BASE}/admin/ubyport/sync-status`, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};
/**
 * Force refresh a specific codebook type
 */
export const forceSyncCodebookType = async (
  type: string,
  token: string
): Promise<UbyportSyncStatusDTO> => {
  const res = await axios.post(`${API_BASE}/admin/ubyport/sync/${type}`, null, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};
/**
 * Force refresh all codebooks
 */
export const forceSyncAllCodebooks = async (token: string): Promise<UbyportSyncStatusDTO[]> => {
  const res = await axios.post(`${API_BASE}/admin/ubyport/sync-all`, null, {
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
};
